import type { AutocompleteState } from "@algolia/autocomplete-core";
import type { AutocompletePlugin } from "@algolia/autocomplete-js";
import type { RecentSearchesPluginData } from "@algolia/autocomplete-plugin-recent-searches";
import type {
    HistoryItem,
    RecentSearchesItem,
} from "@algolia/autocomplete-plugin-recent-searches/dist/esm/types";
import { ProductHits } from "@components/commerce/product-listing/product-tile-list/ProductHits";
import { CommonCMS } from "@lib/constants/contentful";
import { useMicrocopy } from "@lib/contentful/microcopy/MicrocopyContext";
import { Text } from "@ui/components/content/text/Text";
import { Spinner } from "@ui/components/feedback/spinner/Spinner";
import { IconButton } from "@ui/components/forms/icon-button/IconButton";
import { Box } from "@ui/components/layout/box/Box";
import type { FlexProps } from "@ui/components/layout/flex/Flex";
import { Flex } from "@ui/components/layout/flex/Flex";
import { Grid, GridItem } from "@ui/components/layout/grid/Grid";
import { ECCOIcon } from "@ui/components/media-and-icons/ecco-icon/ECCOIcon";
import { NextLink } from "@ui/components/navigation/link/NextLink";
import { defaultUnderlineStyle } from "@ui/css/common";
import { useBoundingRect } from "@ui/hooks/useBoundingRect";
import { useThemeMediaQuery } from "@ui/hooks/useThemeMediaQuery";
import { AnimatePresence, motion } from "framer-motion";
import type { BaseHit, Hit } from "instantsearch.js/es/types";
import { Highlight } from "react-instantsearch";
import { sizes, space } from "ui/src/design-tokens";
import {
    allResultsLinkDesktopStyles,
    allResultsLinkMobileStyles,
    allResultsLinkStyles,
} from "./AutoCompletePanelStyles";
import { FormLabel } from "@ui/components/forms/form-control/FormLabel";

interface AutoCompleteProps {
    autocompleteState:
        | Partial<AutocompleteState<Record<string, any>>>
        | Partial<AutocompleteState<Hit<BaseHit>>>;
    autocomplete: any;
    setIsLoading: any;
    isLoading: boolean;
    searchPagePath: string;
    recentSearchesPlugin: AutocompletePlugin<
        RecentSearchesItem,
        RecentSearchesPluginData<RecentSearchesItem>
    >;
    searchBarOpen: boolean;
}

const SeeAllResultsLink = ({
    url,
    label,
    item,
    addRecentSearch,
    ...rest
}: {
    url: string;
    label: string;
    item?: HistoryItem;
    addRecentSearch: (item: HistoryItem) => void;
} & FlexProps) => {
    return (
        <Flex gap={2} {...rest}>
            <NextLink
                {...allResultsLinkStyles}
                href={url}
                onClick={() => {
                    if (item) {
                        addRecentSearch(item);
                    }
                }}
            >
                {label}
                <ECCOIcon ml={2} name="arrow-right" />
            </NextLink>
        </Flex>
    );
};

export const AutoCompletePanel = ({
    autocompleteState,
    autocomplete,
    setIsLoading,
    isLoading,
    searchPagePath,
    recentSearchesPlugin,
    searchBarOpen,
}: AutoCompleteProps) => {
    const { get: getMicrocopy, getMultiple } = useMicrocopy();
    const { sm, lg, xl } = useThemeMediaQuery();
    const { ref: autocompletePanelRef, dimensions } = useBoundingRect();
    const { query = "", collections } = autocompleteState;

    const addRecentSearch = (item: HistoryItem) => {
        recentSearchesPlugin?.data?.addItem(item);
        autocomplete?.refresh();
    };
    const removeRecentSearch = (id: string) => {
        recentSearchesPlugin?.data?.removeItem(id);
        autocomplete?.refresh();
    };

    const microcopies = getMultiple(CommonCMS.shared, [
        CommonCMS.searchBarRecentSearches,
        CommonCMS.searchBarPopularSearches,
        CommonCMS.searchBarPanelLabel,
    ]);

    return (
        <AnimatePresence initial={false}>
            <motion.div
                className="aa-Panel"
                {...autocomplete?.getPanelProps({})}
                style={{
                    background: "rgba(238, 238, 238, 0.92)",
                    backdropFilter: "blur(7.5px)",
                    position: "absolute",
                    left: "-129px",
                    right: 0,
                    top: sizes.navBarCollapsedHeight,
                    paddingLeft: space["5"],
                    marginTop: space["0.2"],
                    marginBottom: "65px",
                    zIndex: 55,
                    width: "calc(100vw - 75px)",
                    // We need to use this notation for the responsive styles, motion doesn't support Chakra's array/object notation
                    ...(!xl && {
                        width: "100%",
                        left: "0px",
                        paddingLeft: 0,
                    }),
                    ...(!xl &&
                        lg && {
                            paddingLeft: space["3"],
                        }),
                    ...(!sm && {
                        left: 0,
                        right: 0,
                        paddingLeft: 0,
                        width: "100%",
                    }),
                }}
                initial={{ height: 0 }}
                animate={{
                    // We need a min value until the first render, so the animation don't get stuck
                    height: searchBarOpen ? (!dimensions.height ? "auto" : dimensions.height) : 0,
                    mode: "wait",
                }}
                transition={{
                    ease: "easeInOut",
                    duration: 0.4,
                    delay: 0.2,
                }}
                // We need to overwrite the default event listener from Algolia autocomplete component
                onMouseLeave={() => undefined}
            >
                <AnimatePresence>
                    <motion.div
                        initial={{ opacity: 0 }}
                        animate={{
                            opacity: searchBarOpen ? 1 : 0,
                            visibility: searchBarOpen ? "visible" : "hidden",
                            transition: {
                                duration: 0.25,
                                delay: searchBarOpen ? 0.4 : 0,
                            },
                        }}
                        key={`autocompleteGrid-list-key`}
                    >
                        <FormLabel
                            id={autocomplete.getListProps()["aria-labelledby"]}
                            hidden={true}
                        >
                            {microcopies[CommonCMS.searchBarPanelLabel]}
                        </FormLabel>
                        <Grid
                            className="aa-PanelLayout aa-Panel--scrollable"
                            gridTemplateColumns={["1fr", "1fr", "1fr", "1fr 4fr"]}
                            gap={space["0.2"]}
                            ref={autocompletePanelRef}
                        >
                            {isLoading ? (
                                <Box
                                    sx={{
                                        width: "auto",
                                        mr: "25%",
                                        display: "flex",
                                        justifyContent: "center",
                                        opacity: 0.5,
                                        py: 10,
                                        gridColumnStart: 2,
                                    }}
                                >
                                    <Spinner size="xl" />
                                </Box>
                            ) : (
                                collections?.map((collection, index) => {
                                    const { source, items } = collection;
                                    switch (source.sourceId) {
                                        case "recentSearchesPlugin":
                                            return (
                                                !query && (
                                                    <GridItem
                                                        display="flex"
                                                        flexDir="column"
                                                        gridColumnStart="1"
                                                        pt={6}
                                                        pl={[3, 3, 3, 0]}
                                                        {...autocomplete?.getListProps()}
                                                        key={`autocompleteKey-recentSearches${index}`}
                                                    >
                                                        <Text
                                                            tabIndex={0}
                                                            key={`autocompleteText-${index}`}
                                                            fontSize="desktopBodyTextSmall"
                                                            mb={4}
                                                            fontWeight="bold"
                                                            textTransform="uppercase"
                                                            data-testid="autocompleteSearchesHeader"
                                                        >
                                                            {
                                                                microcopies[
                                                                    CommonCMS
                                                                        .searchBarRecentSearches
                                                                ]
                                                            }
                                                        </Text>
                                                        {items?.map(
                                                            (
                                                                {
                                                                    id,
                                                                    label,
                                                                }: { id: string; label: string },
                                                                index: number
                                                            ) => {
                                                                return (
                                                                    <Flex
                                                                        mb={3}
                                                                        key={id + index + "item"}
                                                                        alignItems="center"
                                                                    >
                                                                        <NextLink
                                                                            tabIndex={0}
                                                                            href={`${searchPagePath}?query=${encodeURIComponent(
                                                                                label
                                                                            )}`}
                                                                            onClick={() =>
                                                                                setIsLoading(true)
                                                                            }
                                                                            textTransform="capitalize"
                                                                            textDecor="none"
                                                                            fontSize="desktopHeading5"
                                                                            color="black"
                                                                            fontWeight="semibold"
                                                                            sx={{
                                                                                _hover: {
                                                                                    "--b": "100%",
                                                                                    color: "black",
                                                                                    ...defaultUnderlineStyle,
                                                                                },
                                                                            }}
                                                                        >
                                                                            {label}
                                                                        </NextLink>
                                                                        <IconButton
                                                                            variant="transparent"
                                                                            height={[
                                                                                "100%",
                                                                                "100%",
                                                                            ]}
                                                                            data-testid="removesearchbutton"
                                                                            aria-label="Remove Search"
                                                                            onClick={() =>
                                                                                removeRecentSearch(
                                                                                    id
                                                                                )
                                                                            }
                                                                            icon={
                                                                                <ECCOIcon name="close" />
                                                                            }
                                                                        />
                                                                    </Flex>
                                                                );
                                                            }
                                                        )}
                                                    </GridItem>
                                                )
                                            );
                                        case "querySuggestionsPlugin":
                                            return (
                                                <GridItem
                                                    data-testid="QuerySuggestionsPlugin"
                                                    display="flex"
                                                    flexDirection="column"
                                                    gridColumnStart="1"
                                                    {...autocomplete?.getListProps()}
                                                    py={4}
                                                    key={`autocompleteKey-querySuggestion${index}`}
                                                    pl={[3, 3, 3, 0]}
                                                >
                                                    {!query && (
                                                        <Text
                                                            tabIndex={0}
                                                            fontSize="desktopBodyTextSmall"
                                                            mb={4}
                                                            fontWeight="bold"
                                                            textTransform="uppercase"
                                                            key={`autocompleteKey-qs-text${index}`}
                                                            data-testid="autocompleteSearchesHeader"
                                                        >
                                                            {
                                                                microcopies[
                                                                    CommonCMS
                                                                        .searchBarPopularSearches
                                                                ]
                                                            }
                                                        </Text>
                                                    )}
                                                    {items?.map((item, index) => (
                                                        <NextLink
                                                            mb={3}
                                                            sx={{
                                                                color: "black",
                                                                fontWeight: "semibold",
                                                                "& span": {
                                                                    color: "black",
                                                                    "& mark": {
                                                                        background: "transparent",
                                                                        color: "gray.500",
                                                                    },
                                                                },
                                                                _hover: {
                                                                    "--b": "100%",
                                                                    color: "black",
                                                                    ...defaultUnderlineStyle,
                                                                },
                                                                _active: {
                                                                    color: "black",
                                                                    ...defaultUnderlineStyle,
                                                                },
                                                            }}
                                                            href={`${searchPagePath}?query=${encodeURIComponent(
                                                                item.query
                                                            )}`}
                                                            onClick={() => setIsLoading(true)}
                                                            textTransform="capitalize"
                                                            textDecor="none"
                                                            fontSize={
                                                                query
                                                                    ? "desktopBodyTextSmall"
                                                                    : "desktopHeading5"
                                                            }
                                                            key={item?.objectID + index + "-link"}
                                                        >
                                                            <Highlight
                                                                attribute="query"
                                                                hit={item as Hit<BaseHit>}
                                                            />
                                                        </NextLink>
                                                    ))}
                                                    {query && (
                                                        <SeeAllResultsLink
                                                            key={`see-all-${query}`}
                                                            url={`${searchPagePath}?query=${encodeURIComponent(query)}`}
                                                            label={getMicrocopy(
                                                                CommonCMS.shared,
                                                                CommonCMS.search.seeAll,
                                                                {
                                                                    replacements: {
                                                                        results: `"${query}"`,
                                                                    },
                                                                }
                                                            )}
                                                            item={{
                                                                id: query,
                                                                label: query,
                                                            }}
                                                            addRecentSearch={addRecentSearch}
                                                            {...allResultsLinkDesktopStyles}
                                                        />
                                                    )}
                                                </GridItem>
                                            );
                                        case "products":
                                            return (
                                                <AnimatePresence key={source.sourceId}>
                                                    <motion.div
                                                        data-testid="AutocompleteProductGrid"
                                                        initial={{ opacity: 0 }}
                                                        animate={{
                                                            opacity: searchBarOpen ? 1 : 0,
                                                            visibility: searchBarOpen
                                                                ? "visible"
                                                                : "hidden",
                                                        }}
                                                        key="autocompleteProductGrid-key"
                                                        transition={{
                                                            ease: "easeInOut",
                                                            duration: searchBarOpen ? 0.5 : 0,
                                                            delay: searchBarOpen ? 0.8 : 0,
                                                        }}
                                                        exit={{
                                                            opacity: 0,
                                                            height: 0,
                                                        }}
                                                        style={{
                                                            height: "100%",
                                                        }}
                                                    >
                                                        <Box
                                                            className="aa-Source"
                                                            bg="white"
                                                            overflowX="auto"
                                                            pb={[0, items?.length > 0 && 4]}
                                                        >
                                                            {items?.length > 0 && (
                                                                <Grid
                                                                    data-testid="ProductHitsGrid"
                                                                    gap={space["0.2"]}
                                                                    className="aa-GridItem"
                                                                    {...autocomplete?.getListProps()}
                                                                    gridTemplateColumns={[
                                                                        "1fr 1fr",
                                                                        "1fr 1fr",
                                                                        "1fr 1fr",
                                                                        "1fr 1fr 1fr",
                                                                    ]}
                                                                    key={`autocompleteKey-gridItemProduct${index}`}
                                                                >
                                                                    {items?.length > 0 && (
                                                                        <ProductHits
                                                                            key={`productHits${index}`}
                                                                            productListHits={
                                                                                lg
                                                                                    ? items
                                                                                    : items.slice(
                                                                                          0,
                                                                                          -1
                                                                                      )
                                                                            }
                                                                            isFromSearch={true}
                                                                        />
                                                                    )}
                                                                </Grid>
                                                            )}
                                                            <SeeAllResultsLink
                                                                key={`see-all-${query}`}
                                                                url={`${searchPagePath}?query=${encodeURIComponent(query)}`}
                                                                label={getMicrocopy(
                                                                    CommonCMS.shared,
                                                                    CommonCMS.search.seeAll,
                                                                    {
                                                                        replacements: {
                                                                            results:
                                                                                query &&
                                                                                `"${query}"`,
                                                                        },
                                                                    }
                                                                )}
                                                                item={{
                                                                    id: query,
                                                                    label: query,
                                                                }}
                                                                addRecentSearch={addRecentSearch}
                                                                {...allResultsLinkMobileStyles}
                                                            />
                                                        </Box>
                                                    </motion.div>
                                                </AnimatePresence>
                                            );
                                        default:
                                            return null;
                                    }
                                })
                            )}
                        </Grid>
                    </motion.div>
                </AnimatePresence>
            </motion.div>
        </AnimatePresence>
    );
};
